import type { RequiredTargetingFormType } from '@openx/types/targeting/targetingPresets.ts';
import { ComparisonType, Criteria, Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import cloneDeep from 'lodash/cloneDeep';
import { type RequiredParamsType, RequiredTargetingSectionKeys, TargetingElementKeys } from '../context/types.ts';

const toStringValues = (arr: string[]): string | null =>
  arr.length ? arr.reduce((acc, curr, i) => acc + (i > 0 ? ',' : '') + curr.toString(), '') : null;

export const emptyStructure: RequiredTargetingFormType = {
  op: Criteria.ALL,
  device_type: {
    op: Intersect.INTERSECTS,
    desktop_devices: null,
    mobile_devices: null,
    tv_devices: null,
  },
  distribution_channel: {
    op: Intersect.INTERSECTS,
    val: null,
  },
  ad_placement: {
    op: ComparisonType.EQUALS,
    val: null,
  },
};

export const transformToTargetApiObject = (input: RequiredParamsType): RequiredTargetingFormType => {
  const result = cloneDeep(emptyStructure);

  for (const [sectionKey, section] of Object.entries(input.sections)) {
    if (sectionKey === RequiredTargetingSectionKeys.DEVICE) continue;
    const selectedKeys: string[] = [];

    for (const element of section.elements) {
      if (element.mainOption.isSelected) {
        selectedKeys.push(element.mainOption.key);
      }

      if (element.subOptions) {
        for (const sub of element.subOptions) {
          if (sub.isSelected) {
            selectedKeys.push(sub.key);
          }
        }
      }
    }

    switch (sectionKey) {
      case RequiredTargetingSectionKeys.CHANNEL:
        result.distribution_channel.val = toStringValues(selectedKeys);
        break;
      case RequiredTargetingSectionKeys.FORMAT:
        result.ad_placement.val = selectedKeys[0]?.toString() || null;
        break;
    }
  }

  const excludedKeys: Set<string> = new Set([TargetingElementKeys.DEVICE_MOBILE, TargetingElementKeys.DEVICE_TV]);

  for (const element of input.sections.device.elements) {
    const selectedKeys: string[] = [];

    if (element.mainOption.isSelected && !excludedKeys.has(element.mainOption.key)) {
      selectedKeys.push(element.mainOption.key);
    }

    if (element.subOptions) {
      for (const sub of element.subOptions) {
        if (sub.isSelected && !excludedKeys.has(sub.key)) {
          selectedKeys.push(sub.key);
        }
      }
    }

    switch (element.mainOption.key) {
      case TargetingElementKeys.DEVICE_DESKTOP:
        result.device_type.desktop_devices = toStringValues(selectedKeys);
        break;
      case TargetingElementKeys.DEVICE_TV:
        result.device_type.tv_devices = toStringValues(selectedKeys);
        break;
      case TargetingElementKeys.DEVICE_MOBILE:
        result.device_type.mobile_devices = toStringValues(selectedKeys);
        break;
    }
  }

  return result;
};
